'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { useMessages, useTranslations } from 'next-intl';
import {
  Authenticator as AmplifyAuthenticator,
  AuthenticatorProps as AmplifyAuthenticatorProps,
  Button,
  ThemeProvider,
  translations,
  useAuthenticator,
} from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';
import { signUp } from 'aws-amplify/auth';

import { amplifyTheme } from './theme.amplify';
import { Messages, isNorthAmerica, useCurrentLocale } from '@peerless/ui-locale';
import { Alert, Button as MuiButton, Typography } from '@mui/material';

export interface AuthenticatorProps {
  children?: React.ReactNode;
}

// load translations into Amplify for use in the Authenticator
I18n.putVocabularies(translations);

// configure order of form fields in the sign-up form
const formFields: AmplifyAuthenticatorProps['formFields'] = {
  signUp: {
    given_name: {
      order: 1,
      isRequired: true,
    },
    family_name: {
      order: 2,
      isRequired: true,
    },
    email: {
      order: 3,
      isRequired: true,
    },
    password: {
      order: 4,
    },
    confirm_password: {
      order: 5,
    },
  },
};

/**
 * Wraps a component tree with authentication via Amplify.
 */
export function Authenticator({ children }: AuthenticatorProps) {
  const { locale, language, region } = useCurrentLocale();
  const messages = useMessages() as Messages;

  useEffect(() => {
    // sync the selected locale with Amplify
    // .. amplify includes translations, see for ex., https://ui.docs.amplify.aws/react/connected-components/authenticator/customization#internationalization-i18n
    I18n.setLanguage(language);

    // load any custom translations or labels from our localization dictionary into Amplify
    I18n.putVocabulariesForLanguage(language, messages['Amplify']);
  }, [language, messages]);

  const services: AmplifyAuthenticatorProps['services'] = useMemo(() => {
    return {
      handleSignUp(input) {
        return signUp({
          ...input,
          options: {
            ...input.options,
            userAttributes: {
              ...input.options?.userAttributes,
              // add the current locale to the user's attributes
              locale,
            },
          },
        });
      },
    };
  }, [locale]);
  const t = useTranslations('Account');
  const { toForgotPassword } = useAuthenticator();

  const FormFields = useCallback(() => {
    return (
      <>
        <AmplifyAuthenticator.SignUp.FormFields />
        <Alert severity={'info'}>{t('password-requirements')}</Alert>
      </>
    );
  }, [t]);

  const Footer = useCallback(() => {
    return (
      <>
        <Button fontWeight="bold" onClick={toForgotPassword} size="small" variation="link">
          {t('forgot-password')}
        </Button>
        {isNorthAmerica(region) && (
          <>
            <Typography>
              <Typography fontWeight={'bold'}>{t('business-1')}</Typography>
              {t('business-2')}
            </Typography>

            <MuiButton
              href="https://peerless-av-portal.acumatica.com/Frames/Login.aspx"
              target="_blank"
              variant="outlined"
              color="secondary"
            >
              {t('b2b-login')}
            </MuiButton>
          </>
        )}
      </>
    );
  }, [t, region, toForgotPassword]);

  return (
    <ThemeProvider theme={amplifyTheme}>
      <AmplifyAuthenticator.Provider>
        <AmplifyAuthenticator
          components={{
            SignUp: {
              FormFields,
            },
            SignIn: {
              Footer,
            },
          }}
          formFields={formFields}
          services={services}
        >
          {children}
        </AmplifyAuthenticator>
      </AmplifyAuthenticator.Provider>
    </ThemeProvider>
  );
}
